import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  IconButton
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Close } from "@mui/icons-material";
import { Product } from "../types/entities";
import { ProductFormData } from "../types/forms";

interface ProductFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ProductFormData) => Promise<void>;
  product?: Product | null;
  loading?: boolean;
  error?: string | null;
  initialName?: string;
}

const ProductFormModal: React.FC<ProductFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  product,
  loading = false,
  error = null,
  initialName,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ProductFormData>({
    name: "",
    amount: 0,
    sell_price: "",
    description: "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof ProductFormData, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof ProductFormData, boolean>>>({});

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        amount: product.amount,
        sell_price: product.sell_price || "",
        description: product.description || "",
      });
    } else {
      setFormData({
        name: initialName || "",
        amount: 0,
        sell_price: "",
        description: "",
      });
    }
    setErrors({});
    setTouched({});
  }, [product, open, initialName]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ProductFormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = t("validation.required");
    }

    if (formData.amount < 0) {
      newErrors.amount = t("validation.minValue", { min: 0 });
    }

    if (formData.sell_price && parseFloat(formData.sell_price) < 0) {
      newErrors.sell_price = t("validation.minValue", { min: 0 });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if form is valid for button state
  const isFormValid = () => {
    return formData.name.trim() !== "" && 
           formData.amount >= 0 && 
           (!formData.sell_price || parseFloat(formData.sell_price) >= 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const submitData: ProductFormData = {
        name: formData.name.trim(),
        amount: formData.amount,
        sell_price: formData.sell_price || undefined,
        description: formData.description || undefined,
      };

      await onSubmit(submitData);
    } catch (err) {
      // Error is handled by parent component
    }
  };

  const handleInputChange =
    (field: keyof ProductFormData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      if (field === "amount") {
        setFormData((prev) => ({ ...prev, [field]: parseInt(value) || 0 }));
      } else {
        setFormData((prev) => ({ ...prev, [field]: value }));
      }

      // Mark field as touched
      setTouched((prev) => ({ ...prev, [field]: true }));
    };

  const handleBlur = (field: keyof ProductFormData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // Real-time validation - only show errors for touched fields
  useEffect(() => {
    const newErrors: Partial<Record<keyof ProductFormData, string>> = {};

    if (touched.name && formData.name.trim() === "") {
      newErrors.name = t("validation.required");
    }

    if (touched.amount && formData.amount < 0) {
      newErrors.amount = t("validation.minValue", { min: 0 });
    }

    if (touched.sell_price && formData.sell_price && parseFloat(formData.sell_price) < 0) {
      newErrors.sell_price = t("validation.minValue", { min: 0 });
    }

    setErrors(newErrors);
  }, [formData, touched, t]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {product ? t("products.editProduct") : t("products.createProduct")}
        <IconButton
          onClick={onClose}
          disabled={loading}
          size="small"
          sx={{ 
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary'
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label={t("common.name")}
              value={formData.name}
              onChange={handleInputChange("name")}
              onBlur={() => handleBlur("name")}
              error={!!errors.name}
              helperText={errors.name}
              required
              fullWidth
              disabled={loading}
            />

            <TextField
              label={t("products.amount")}
              type="number"
              value={formData.amount}
              onChange={handleInputChange("amount")}
              onBlur={() => handleBlur("amount")}
              error={!!errors.amount}
              helperText={errors.amount}
              required
              fullWidth
              disabled={loading}
              inputProps={{ min: 0, step: 1 }}
            />

            <TextField
              label={`${t("products.sellPrice")} (zł)`}
              type="number"
              value={formData.sell_price}
              onChange={handleInputChange("sell_price")}
              onBlur={() => handleBlur("sell_price")}
              error={!!errors.sell_price}
              helperText={errors.sell_price}
              fullWidth
              disabled={loading}
              inputProps={{ min: 0, step: 0.01 }}
            />

            <TextField
              label={t("common.description")}
              value={formData.description}
              onChange={handleInputChange("description")}
              multiline
              rows={3}
              fullWidth
              disabled={loading}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !isFormValid()}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading
              ? t("common.saving")
              : product
              ? t("common.update")
              : t("common.create")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProductFormModal;
